import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, CreditCard, Loader2 } from "lucide-react";

/**
 * Reuse an offer into another campaign (spec 10.5).
 *
 * Reuse is a COPY, not a share - the endpoint mints a new offer carrying the same
 * lineage_id. That is what lets spec 9's "an offer belongs to exactly one campaign"
 * and spec 10.5's reuse workflow both be true at once.
 *
 * The backend already enforces every rule: tier offer limits, lineage duplication, and
 * Discovery Boost entitlement. This dialog deliberately does NOT re-implement any of
 * them - it prompts for a target and renders whatever the backend says.
 *
 * **The refusal messages are shown verbatim.** Spec 10.5.3 and 10.5.4 specify the exact
 * wording, and campaign_rules holds it (OFFER_DUPLICATE_MESSAGE, OFFER_LIMIT_MESSAGE).
 * Re-wording it here would put two copies of specified customer-facing text in the
 * codebase, which is how they drift.
 */

export default function ReuseOfferDialog({ open, onOpenChange, offer, campaigns = [], onDone }) {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [targetId, setTargetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [needsBoost, setNeedsBoost] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTargetId("");
    setSaving(false);
    setError(null);
    setNeedsBoost(false);
    setSuccess(null);
  }, [open, offer]);

  // Reusing into the offer's own campaign is always refused (same lineage), so it is
  // not offered - a choice that can only fail is not a choice.
  const targets = campaigns.filter((c) => c.id !== offer?.campaign_id);
  const targetName = targets.find((c) => c.id === targetId)?.name;

  const submit = async () => {
    if (!targetId) return setError("Choose a campaign to add this offer to.");
    setSaving(true);
    setError(null);
    setNeedsBoost(false);
    try {
      await axios.post(
        `${API}/offers/${offer.id}/reuse`,
        {},
        { ...auth, params: { target_campaign_id: targetId } }
      );
      setSuccess(targetName || "the campaign");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 402 means the target campaign has not bought Discovery Boost. That is a
      // purchasable state, not a dead end, so it reads differently from a limit refusal.
      if (e?.response?.status === 402) setNeedsBoost(true);
      setError(detail || "Could not reuse this offer.");
    } finally {
      setSaving(false);
    }
  };

  // spec 10.5.5: confirmation with View Campaign / Done.
  if (success) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Offer successfully added to {success}
            </DialogTitle>
            <DialogDescription>
              A copy now belongs to that campaign. Editing it there will not affect the
              original.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { onOpenChange(false); onDone?.(targetId); }}>
              View Campaign
            </Button>
            <Button onClick={() => { onOpenChange(false); onDone?.(null); }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reuse this offer</DialogTitle>
          <DialogDescription>
            &ldquo;{offer?.display_value}&rdquo; will be copied into another campaign.
            The original stays where it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Add to campaign</Label>
          {targets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You have no other campaigns to add this offer to.
            </p>
          ) : (
            <Select value={targetId} onValueChange={(v) => { setTargetId(v); setError(null); setNeedsBoost(false); }}>
              <SelectTrigger><SelectValue placeholder="Choose a campaign" /></SelectTrigger>
              <SelectContent>
                {targets.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.tier ? `(${c.tier.charAt(0)}${c.tier.slice(1).toLowerCase()})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {error && (
          <div className={`flex items-start gap-2 text-sm rounded-md p-3 ${
            needsBoost
              ? "bg-purple-500/10 text-purple-700 dark:text-purple-300"
              : "bg-destructive/10 text-destructive"
          }`}>
            {needsBoost
              ? <CreditCard className="h-4 w-4 mt-0.5 shrink-0" />
              : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
            {/* Verbatim from the backend - spec 10.5.3/10.5.4 specify this wording. */}
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !targetId || targets.length === 0}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Add offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
